/**
 * Page Paramètres > Formateurs (Rafik 03/06/2026).
 *
 * Liste tous les formateurs avec leurs données complètes (Zoho + extras admin
 * cockpit). Bouton "Nouveau formateur" → modal formulaire. Click sur une ligne
 * → modal édition.
 *
 * Les 4 champs admin (statut juridique, SIRET, NDA, adresse) sont stockés en
 * BDD cockpit (`FormateurExtra`) car pas encore créés côté module Zoho
 * Formateurs. À migrer vers Zoho plus tard si besoin.
 */
import { useState } from 'react';
import { Loader2, AlertCircle, Plus, Edit, X, Save, Check } from 'lucide-react';
import {
  useFormateurs,
  useCreateFormateur,
  useUpdateFormateur,
  type FormateurFull,
  type CreateFormateurInput,
} from '../hooks/useFormateurs.js';

export function FormateursPage() {
  const { data, isLoading, error } = useFormateurs();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<FormateurFull | null>(null);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-primary-500 text-xs font-semibold uppercase tracking-wider">
            Paramètres
          </div>
          <h1 className="text-primary-900 text-2xl font-bold">
            Formateurs & Assistants pédagogiques
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Gère les intervenants RK Formations. Les données ici alimentent automatiquement la
            génération des contrats (cadre + missions).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded px-3 py-2 text-sm font-medium text-white"
        >
          <Plus className="h-4 w-4" />
          Nouveau formateur
        </button>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 rounded border border-neutral-200 bg-white p-6 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des formateurs…
        </div>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <AlertCircle className="h-4 w-4" />
            Erreur de chargement
          </div>
          {(error as Error).message}
        </div>
      )}

      {data && <FormateursTable formateurs={data.formateurs} onEdit={(f) => setEditing(f)} />}

      {showCreate && (
        <FormateurModal mode="create" initial={null} onClose={() => setShowCreate(false)} />
      )}

      {editing && <FormateurModal mode="edit" initial={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

function FormateursTable({
  formateurs,
  onEdit,
}: {
  formateurs: FormateurFull[];
  onEdit: (f: FormateurFull) => void;
}) {
  if (formateurs.length === 0) {
    return (
      <div className="rounded border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
        Aucun formateur enregistré. Crée le premier avec « Nouveau formateur ».
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-neutral-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-600">
          <tr>
            <th className="px-3 py-2 text-left">Nom / Prénom</th>
            <th className="px-3 py-2 text-left">Rôle</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-right">Tarif</th>
            <th className="px-3 py-2 text-center">Admin OK ?</th>
            <th className="px-3 py-2 text-center">Statut</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {formateurs.map((f) => {
            const adminOk = !!(f.statutJuridique && f.siret && f.adresse);
            const isFormateur = isFormateurRole(f.roles);
            return (
              <tr key={f.id} className="hover:bg-neutral-50">
                <td className="px-3 py-2 font-medium text-neutral-900">
                  {f.nom} {f.prenom}
                </td>
                <td className="px-3 py-2 text-neutral-700">
                  {isFormateur ? 'Formateur' : 'Assistant péda.'}
                </td>
                <td className="px-3 py-2 text-neutral-700">
                  <a href={`mailto:${f.email}`} className="text-primary-600 hover:underline">
                    {f.email}
                  </a>
                </td>
                <td className="px-3 py-2 text-right text-neutral-700">
                  {f.tarifHoraire > 0 ? `${f.tarifHoraire} €/h` : '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  {adminOk ? (
                    <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      <Check className="h-3 w-3" />
                      OK
                    </span>
                  ) : (
                    <span
                      title="Manque statut juridique, SIRET ou adresse"
                      className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    >
                      <AlertCircle className="h-3 w-3" />À compléter
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-xs text-neutral-600">
                  {f.statut || '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onEdit(f)}
                    className="hover:bg-primary-50 hover:text-primary-700 inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-neutral-600"
                  >
                    <Edit className="h-3 w-3" />
                    Éditer
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function isFormateurRole(roles: string[]): boolean {
  return !roles.some((r) => r.toLowerCase().includes('assistant') && r.toLowerCase().includes('p'));
}

// ─── Modal Form ──────────────────────────────────────────────────────────────

interface ModalProps {
  mode: 'create' | 'edit';
  initial: FormateurFull | null;
  onClose: () => void;
}

function FormateurModal({ mode, initial, onClose }: ModalProps) {
  const createMutation = useCreateFormateur();
  const updateMutation = useUpdateFormateur();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  const [form, setForm] = useState<CreateFormateurInput>(() => ({
    nom: initial?.nom ?? '',
    prenom: initial?.prenom ?? '',
    email: initial?.email ?? '',
    telephone: initial?.telephone ?? '',
    tarifHoraire: initial?.tarifHoraire ?? undefined,
    roles: initial?.roles ?? ['Formateur'],
    statut: initial?.statut ?? 'Actif',
    typeNDA: initial?.typeNDA ?? '',
    statutJuridique: initial?.statutJuridique ?? '',
    siret: initial?.siret ?? '',
    nda: initial?.nda ?? '',
    adresse: initial?.adresse ?? '',
  }));

  const handleChange =
    (field: keyof CreateFormateurInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleRoleChange = (newRole: 'Formateur' | 'Assistant pédagogique') => {
    setForm((prev) => ({ ...prev, roles: [newRole] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) return;

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(form);
      } else if (initial) {
        await updateMutation.mutateAsync({ id: initial.id, patch: form });
      }
      onClose();
    } catch {
      // Garde la modale ouverte pour afficher l'erreur
    }
  };

  const currentRole = form.roles?.[0] ?? 'Formateur';
  const isFormateurType = currentRole === 'Formateur';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h2 className="text-base font-semibold text-neutral-900">
            {mode === 'create' ? 'Nouveau formateur' : `Éditer ${initial?.nom} ${initial?.prenom}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5 px-5 py-4">
          {/* Rôle */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600">
              Rôle
            </label>
            <div className="flex gap-2">
              <RoleBtn
                label="Formateur"
                active={isFormateurType}
                onClick={() => handleRoleChange('Formateur')}
              />
              <RoleBtn
                label="Assistant pédagogique"
                active={!isFormateurType}
                onClick={() => handleRoleChange('Assistant pédagogique')}
              />
            </div>
          </div>

          {/* Identité */}
          <Section title="Identité">
            <Row>
              <Field label="Nom *" required>
                <input
                  type="text"
                  value={form.nom}
                  onChange={handleChange('nom')}
                  className={inputCls}
                />
              </Field>
              <Field label="Prénom *" required>
                <input
                  type="text"
                  value={form.prenom}
                  onChange={handleChange('prenom')}
                  className={inputCls}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Email *" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  className={inputCls}
                />
              </Field>
              <Field label="Téléphone">
                <input
                  type="tel"
                  value={form.telephone ?? ''}
                  onChange={handleChange('telephone')}
                  className={inputCls}
                />
              </Field>
            </Row>
          </Section>

          {/* Activité */}
          <Section title="Activité">
            <Row>
              <Field label="Tarif horaire (€)">
                <input
                  type="number"
                  step="0.01"
                  value={form.tarifHoraire ?? ''}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      tarifHoraire: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                  className={inputCls}
                />
              </Field>
              <Field label="Statut">
                <select
                  value={form.statut ?? ''}
                  onChange={handleChange('statut')}
                  className={inputCls}
                >
                  <option value="Actif">Actif</option>
                  <option value="Inactif">Inactif</option>
                  <option value="Test">Test</option>
                </select>
              </Field>
            </Row>
            {isFormateurType && (
              <Row>
                <Field label="Type NDA (qualification Qualiopi)">
                  <select
                    value={form.typeNDA ?? ''}
                    onChange={handleChange('typeNDA')}
                    className={inputCls}
                  >
                    <option value="">— Non défini —</option>
                    <option value="Qualiopi Actions de formation">
                      Qualiopi Actions de formation (CPF)
                    </option>
                    <option value="Indépendant">Indépendant (Direct/OPCO)</option>
                  </select>
                </Field>
              </Row>
            )}
          </Section>

          {/* Administratif pour contrats */}
          <Section
            title="Administratif (pour contrats)"
            hint="Ces champs sont stockés côté cockpit et alimentent automatiquement les contrats générés."
          >
            <Row>
              <Field label="Statut juridique">
                <select
                  value={form.statutJuridique ?? ''}
                  onChange={handleChange('statutJuridique')}
                  className={inputCls}
                >
                  <option value="">— Non défini —</option>
                  <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                  <option value="Micro-entreprise">Micro-entreprise</option>
                  <option value="SASU">SASU</option>
                  <option value="EURL">EURL</option>
                  <option value="SARL">SARL</option>
                  <option value="EI">Entreprise individuelle</option>
                  <option value="Salarié porté">Salarié porté</option>
                </select>
              </Field>
              <Field label="SIRET">
                <input
                  type="text"
                  value={form.siret ?? ''}
                  onChange={handleChange('siret')}
                  placeholder="14 chiffres"
                  className={inputCls}
                />
              </Field>
            </Row>
            {isFormateurType && (
              <Row>
                <Field label="Numéro de déclaration d'activité (NDA)">
                  <input
                    type="text"
                    value={form.nda ?? ''}
                    onChange={handleChange('nda')}
                    placeholder="ex: 84692544169"
                    className={inputCls}
                  />
                </Field>
              </Row>
            )}
            <Row>
              <Field label="Adresse (postale complète)" full>
                <textarea
                  value={form.adresse ?? ''}
                  onChange={handleChange('adresse')}
                  rows={2}
                  placeholder="Numéro, voie, code postal, ville"
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </Row>
          </Section>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-neutral-100 pt-4">
            <div className="flex-1 text-sm">
              {error && (
                <div className="flex items-center gap-1.5 text-rose-700">
                  <AlertCircle className="h-4 w-4" />
                  {(error as Error).message}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-neutral-300"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {mode === 'create' ? 'Créer' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function RoleBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded border px-3 py-2 text-sm transition-colors ${
        active
          ? 'border-primary-400 bg-primary-50 text-primary-900 font-medium'
          : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
      }`}
    >
      {label}
    </button>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-600">
        {title}
      </h3>
      {hint && <p className="mb-2 text-xs text-neutral-500">{hint}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-100';
